import { useCallback, useState } from 'react';
import { ENTERPRISE_SITE_KEY_V3 } from '../useRecaptcha/constants';
import { getSDK, recaptchaSDK } from '../ScriptLoader';
import type { ReCaptchaV3 } from '../useRecaptcha/types';
import type { FormType } from '../../../../types/types';

export interface UseInvisibleCaptchaOptions {
  /**
   * Language for the reCAPTCHA widget
   */
  language?: string;
  /**
   * Action name for the invisible captcha (used for analytics/scoring).
   * Must match the form type ('login' or 'signup').
   */
  action: FormType;
  /**
   * Whether to enable invisible captcha.
   * When false, the SDK will not be loaded and getToken will return undefined.
   * This prevents the reCAPTCHA badge from appearing when only visible captcha is needed.
   * Defaults to true.
   */
  enabled?: boolean;
}

export interface UseInvisibleCaptchaReturn {
  /**
   * Execute the invisible captcha and return a token
   * Returns undefined if execution fails
   */
  getToken: () => Promise<string | undefined>;
  /**
   * Whether the captcha SDK is currently loading
   */
  isLoading: boolean;
  /**
   * Any error that occurred during captcha execution
   */
  error: Error | null;
}

/**
 * Hook for obtaining invisible reCAPTCHA tokens.
 *
 * Unlike visible captcha, invisible captcha doesn't require user interaction.
 * The token should be obtained just before making the API call (login/register)
 * and passed to the backend for verification.
 *
 * Usage:
 * ```tsx
 * const { getToken, isLoading, error } = useInvisibleCaptcha({
 *   language: 'en',
 *   action: 'login'  // FormType: 'login' | 'signup'
 * });
 *
 * const handleLogin = async () => {
 *   const invisibleRecaptchaToken = await getToken();
 *   await login(email, password, { invisibleRecaptchaToken });
 * };
 * ```
 */
export const useInvisibleCaptcha = ({
  language,
  action,
  enabled = true,
}: UseInvisibleCaptchaOptions): UseInvisibleCaptchaReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Only load the SDK when enabled to prevent the reCAPTCHA badge from appearing
  // when only visible captcha is needed (e.g., recaptchaOption === 'ALWAYS')
  const [sdkPromise] = useState(() => {
    if (!enabled) {
      return null;
    }
    return getSDK<ReCaptchaV3>(recaptchaSDK, {
      hl: language,
      key: ENTERPRISE_SITE_KEY_V3,
      useV3: true,
    }).catch((err) => {
      console.error('Failed to preload invisible captcha:', err);
      throw err;
    });
  });

  const getToken = useCallback(async (): Promise<string | undefined> => {
    // Return undefined if invisible captcha is disabled
    if (!sdkPromise) {
      return undefined;
    }

    setIsLoading(true);
    setError(null);

    try {
      const sdk = await sdkPromise;

      return await new Promise<string>((resolve, reject) => {
        sdk.ready(async () => {
          try {
            const captchaToken = await sdk.execute(ENTERPRISE_SITE_KEY_V3, {
              action,
            });
            resolve(captchaToken);
          } catch (executeError) {
            reject(executeError);
          }
        });
      });
    } catch (err) {
      const captchaError =
        err instanceof Error
          ? err
          : new Error('Failed to get invisible captcha token');
      setError(captchaError);
      console.error('Invisible captcha error:', captchaError);
      return undefined;
    } finally {
      setIsLoading(false);
    }
  }, [sdkPromise, action]);

  return {
    getToken,
    isLoading,
    error,
  };
};
